import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Container } from "react-bootstrap";
import { useNavigationType, useLocation } from "react-router-dom";


import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
import { approveOrRejectUser } from "../../../redux/slice/userSlice";
import { useLoader } from "../../../context/LoaderContext";
import PasswordChangeModal from "../../../components/modals/PasswordChangeModal";
import { allUsersList, fetchAppUserStats, fetchProfessions, changeUserPassword } from "../../../redux/slice/allUsersSlice";
import { fetchActiveCities } from "../../../redux/slice/locationSlice";
import { fetchPlans } from "../../../redux/slice/subscriptionSlice";
import ListSection from "./ListSection";
import { useToast } from "../../../context/TostContext";
import { UsersIcon, ApprovedIcon } from "../../../assets/icons";
import FilterModal from "../../../components/FilterModal/FilterModal";
import { TabCard } from "../../../components/custom-card/Cards";


const AllUsers = () => {
  const dispatch = useDispatch();
  const { setLoading } = useLoader();
  const navigationType = useNavigationType();
  const location = useLocation();
  const toast = useToast();
  // Pagination
  const [limit, setLimit] = useState(() => {
    if (navigationType === "POP") {
      const savedLimit = sessionStorage.getItem("allUsers_limit");
      if (savedLimit) return JSON.parse(savedLimit);
    }
    return { value: 10, label: 10 };
  });
  const [currentPage, setCurrentPage] = useState(() => {
    if (navigationType === "POP") {
      const savedPage = sessionStorage.getItem("allUsers_page");
      if (savedPage) return parseInt(savedPage, 10);
    }
    return 1;
  });
  // Open Add User Modal

  // Open ChangePassword Modal
  const [openChangePwModal, setOpenChangePwModal] = useState(false);
  const [changePasswordId, setChangePasswordId] = useState("");
  // Card Status
  const [cardStatus, setCardStatus] = useState(() => {
    if (location.pathname === '/user/r-point') return "not_migrated";
    if (location.pathname === '/user/mep') return "migrated";
    return sessionStorage.getItem("allUsers_cardStatus") || "";
  });

  // Search Bar
  const [searchTerm, setSearchTerm] = useState(() => {
    if (navigationType === "POP") {
      return sessionStorage.getItem("allUsers_searchTerm") || "";
    }
    return "";
  });
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedProfessions, setSelectedProfessions] = useState([]);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [filterModal, setFilterModal] = useState(false);
  const prevPathnameRef = useRef(location.pathname);


  const {
    allUsers = [],
    isLoading,
    appUserStats,
    professions = [],
  } = useSelector((state) => state.allUsers || {});

  const { plans = [] } = useSelector((state) => state.subscription || {});

  // const { cities = [] } = useSelector((state) => state.location || {});

  const { cities = [] } = useSelector((state) => state.location || {});

  const cityOptions = useMemo(() => {
    const fetchedOptions = cities.map(c => ({ value: c.id, label: c.name }));
    const allOptions = [...selectedCities, ...fetchedOptions];
    const map = new Map();
    const uniqueOptions = [];
    for (const item of allOptions) {
      if (item && item.value && !map.has(item.value)) {
        map.set(item.value, true);
        uniqueOptions.push(item);
      }
    }
    return uniqueOptions;
  }, [cities, selectedCities]);
  const professionOptions = useMemo(() => professions.map(p => ({ value: p.id, label: p.name })), [professions]);
  const planOptions = useMemo(() => {
    const fetchedOptions = plans.map(p => ({ value: p.id, label: p.name }));
    const allOptions = [...selectedPlans, { value: "free", label: "Free" }, ...fetchedOptions];
    const map = new Map();
    const uniqueOptions = [];
    for (const item of allOptions) {
      if (item && item.value && !map.has(item.value)) {
        map.set(item.value, true);
        uniqueOptions.push(item);
      }
    }
    return uniqueOptions;
  }, [plans, selectedPlans]);

  // console.log("Users:", allUsers);
  // console.log("Status Counts:", statusCounts);
  // console.log("Total Users:", totalUsers);

  const AllTypesOfUserCard = [
    {
      id: 1,
      total: appUserStats?.total || 0,
      title: "Total Users",
      icon: <UsersIcon size={24} />,
      status: "",
      isVerified: false,
      iconClass: "icon-info",
    },
    {
      id: 2,
      total: appUserStats?.active || 0,
      title: "Active Users",
      icon: <UsersIcon size={24} />,
      status: "active",
      isVerified: false,
      iconClass: "icon-success",
    },
    {
      id: 3,
      total: appUserStats?.inactive || 0,
      title: "Inactive Users",
      icon: <UsersIcon size={24} />,
      status: "inactive",
      isVerified: false,
      iconClass: "icon-warning",
    },
    {
      id: 4,
      total: appUserStats?.verified || 0,
      title: "Verified Users",
      icon: <ApprovedIcon size={24} />,
      status: "verified",
      iconClass: "icon-success",
    },
    {
      id: 5,
      total: appUserStats?.migrated || 0,
      title: "MEP Users",
      icon: <UsersIcon size={24} />,
      status: "migrated",
      iconClass: "icon-purple",
    },
    {
      id: 6,
      total: appUserStats?.not_migrated || 0,
      title: "Direct Signups (R-Point)",
      icon: <UsersIcon size={24} />,
      status: "not_migrated",
      iconClass: "icon-info",
    },
    {
      id: 7,
      total: appUserStats?.migrated_incomplete || 0,
      title: <>Migrated Users <br />(Pending Registration)</>,
      icon: <UsersIcon size={24} />,
      status: "migrated_incomplete",
      iconClass: "icon-warning",
    },
    {
      id: 8,
      total: appUserStats?.migrated_completed || 0,
      title: <>Migrated Users <br />(Registration Completed)</>,
      icon: <UsersIcon size={24} />,
      status: "migrated_completed",
      iconClass: "icon-success",
    }
  ];

  const rPointCards = [
    {
      id: 1,
      total: appUserStats?.rPoint?.total || 0,
      title: "Total R-Point Users",
      icon: <UsersIcon size={24} />,
      status: "not_migrated",
      isVerified: false,
      iconClass: "icon-info",
    },
    {
      id: 2,
      total: appUserStats?.rPoint?.active || 0,
      title: "Active Users",
      icon: <UsersIcon size={24} />,
      status: "active",
      isVerified: false,
      iconClass: "icon-success",
    },
    {
      id: 3,
      total: appUserStats?.rPoint?.inactive || 0,
      title: "Inactive Users",
      icon: <UsersIcon size={24} />,
      status: "inactive",
      isVerified: false,
      iconClass: "icon-warning",
    },
    {
      id: 4,
      total: appUserStats?.rPoint?.verified || 0,
      title: "Verified Users",
      icon: <ApprovedIcon size={24} />,
      status: "verified",
      iconClass: "icon-success",
    },
  ];

  const mepCards = [
    {
      id: 1,
      total: appUserStats?.mep?.total || 0,
      title: "Total MEP Users",
      icon: <UsersIcon size={24} />,
      status: "migrated",
      isVerified: false,
      iconClass: "icon-purple",
    },
    {
      id: 2,
      total: appUserStats?.mep?.active || 0,
      title: "Active Users",
      icon: <UsersIcon size={24} />,
      status: "active",
      isVerified: false,
      iconClass: "icon-success",
    },
    {
      id: 3,
      total: appUserStats?.mep?.inactive || 0,
      title: "Inactive Users",
      icon: <UsersIcon size={24} />,
      status: "inactive",
      isVerified: false,
      iconClass: "icon-warning",
    },
    {
      id: 4,
      total: appUserStats?.mep?.verified || 0,
      title: "Verified Users",
      icon: <ApprovedIcon size={24} />,
      status: "verified",
      iconClass: "icon-success",
    },
    {
      id: 5,
      total: appUserStats?.mep?.incomplete || 0,
      title: <>Migrated Users <br />(Pending Registration)</>,
      icon: <UsersIcon size={24} />,
      status: "migrated_incomplete",
      iconClass: "icon-warning",
    },
    {
      id: 6,
      total: appUserStats?.mep?.completed || 0,
      title: <>Migrated Users <br />(Registration Completed)</>,
      icon: <UsersIcon size={24} />,
      status: "migrated_completed",
      iconClass: "icon-success",
    }
  ];

  let displayCards = AllTypesOfUserCard;
  if (location.pathname === '/user/r-point') {
    displayCards = rPointCards;
  } else if (location.pathname === '/user/mep') {
    displayCards = mepCards;
  }

  // Helper to resolve pseudo statuses
  const resolveStatusParams = useCallback((currentCardStatus) => {
    const pseudoStatuses = ['migrated', 'not_migrated', 'migrated_incomplete', 'migrated_completed', 'verified'];
    const isPseudoStatus = pseudoStatuses.includes(currentCardStatus);

    let isMigratedParam = undefined;
    let mustResetPasswordParam = undefined;
    let verificationStatusParam = undefined;

    // Apply strict filters based on current URL routing
    if (location.pathname === '/user/r-point') {
      isMigratedParam = false;
    } else if (location.pathname === '/user/mep') {
      isMigratedParam = true;
    }

    if (currentCardStatus === 'migrated') isMigratedParam = true;
    else if (currentCardStatus === 'not_migrated') isMigratedParam = false;
    else if (currentCardStatus === 'migrated_incomplete') { isMigratedParam = true; mustResetPasswordParam = true; }
    else if (currentCardStatus === 'migrated_completed') { isMigratedParam = true; mustResetPasswordParam = false; }
    else if (currentCardStatus === 'verified') verificationStatusParam = 'verified';

    return {
      status: isPseudoStatus ? "" : currentCardStatus,
      is_migrated: isMigratedParam,
      must_reset_password: mustResetPasswordParam,
      ...(verificationStatusParam && { verification_status: verificationStatusParam })
    };
  }, [location.pathname]);

  // Pagination Handler
  const handleChange = (option) => {
    if (!option) return
    setLimit(option);
    setCurrentPage(1);
  };



  // status handler based on Card Click (APi will get call and display list based on status)
  const handleCardClick = (item) => {
    setCardStatus(item.status);
    setCurrentPage(1);
  };

  // Open Add User Modal
  const addUserHandler = () => {
    // setOpenAddUserModal(true);
  };

  const getCustomerActions = (item) => {
    if (!item || !item.status) {
      return []; // Return an empty array if the item or status is invalid
    }

    const actions = [];

    if (item.status === "pending") {
      // Show both Approve and Reject buttons
      actions.push(
        {
          title: "Approve",
          handler: () => handleUserApproveOrReject("approved", item),
        },
        {
          title: "Reject",
          handler: () => handleUserApproveOrReject("rejected", item),
        }
      );
    } else if (item.status === "approved") {
      // Show only the Reject button
      actions.push({
        title: "Reject",
        handler: () => handleUserApproveOrReject("rejected", item),
      });
    } else if (item.status === "rejected") {
      // Show only the Approve button
      actions.push({
        title: "Approve",
        handler: () => handleUserApproveOrReject("approved", item),
      });
    }

    // Actions always available
    actions.push(
      // {
      //   title: "Deactivate",
      //   handler: () => handleDeactivateUser(item),
      //   style: { color: "red" },
      // },
      {
        title: "Change Password",
        handler: () => handleChangePassword(item),
      }
    );
    return actions;
  };

  const handleChangePassword = (item) => {
    setChangePasswordId(item);
    setOpenChangePwModal(true);
  };

  // Approve or Reject User
  const handleUserApproveOrReject = (type, item) => {
    try {
      const id = item?.id;
      if (type && id) {
        setLoading(true);
        const data = { status: type };
        dispatch(approveOrRejectUser({ data, id })).then((res) => {
          toast.success(res?.payload.message || "User status updated successfully!");
          if (res?.payload?.user) {
            dispatch(allUsersList({
              ...resolveStatusParams(cardStatus),
              page: currentPage,
              city_id: selectedCities.map(c => c.value),
              profession_id: selectedProfessions.map(p => p.value),
              plan_id: selectedPlans.map(p => p.value),
              limit: limit?.value,
              search: searchTerm
            })).then(() => {
              setTimeout(() => {
                dispatch(fetchAppUserStats());
              }, 1500);
            });
          }
        });
      } else {
        setLoading(false);
        toast.error("User status or Id Missing!");
      }
    } catch (err) {
      setLoading(false);
      const errorMessage = err?.message || "Failed to update user status!";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isInitialLoadRef = useRef(true);
  const searchTimeout = useRef(null);
  const citySearchTimeoutRef = useRef(null);

  const handleCitySearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (citySearchTimeoutRef.current) clearTimeout(citySearchTimeoutRef.current);
      citySearchTimeoutRef.current = setTimeout(() => {
        dispatch(fetchActiveCities({ search: inputValue, limit: 20 }));
      }, 400);
    } else if (action === "menu-close") {
      dispatch(fetchActiveCities({ limit: 20 }));
    }
  };

  useEffect(() => {
    dispatch(fetchAppUserStats());
    dispatch(fetchActiveCities());
    dispatch(fetchProfessions());
    dispatch(fetchPlans({ limit: 100 }));
  }, [dispatch]);

  // Handle auto-updating cardStatus on route switch
  useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      setSelectedCities([]);
      setSelectedProfessions([]);
      setSelectedPlans([]);
      setAdvancedFilters({});
      setSearchTerm("");
      prevPathnameRef.current = location.pathname;
    }

    if (location.pathname === '/user/r-point') {
      setCardStatus("not_migrated");
      setCurrentPage(1);
    } else if (location.pathname === '/user/mep') {
      setCardStatus("migrated");
      setCurrentPage(1);
    }
  }, [location.pathname]);


  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    const fetchData = () => {
      if (isInitialLoadRef.current) {
        setLoading(true);
      }

      dispatch(
        allUsersList({
          search: searchTerm,
          page: currentPage,
          ...resolveStatusParams(cardStatus),
          limit: limit?.value,
          city_id: selectedCities.map(c => c.value),
          profession_id: selectedProfessions.map(p => p.value),
          plan_id: selectedPlans.map(p => p.value),
          advancedFilters,
        })
      ).finally(() => {
        if (isInitialLoadRef.current) {
          setLoading(false);
          isInitialLoadRef.current = false;
        }
      });
    };

    if (isInitialLoadRef.current) {
      fetchData();
    } else {
      searchTimeout.current = setTimeout(fetchData, 300);
    }

    // Save to sessionStorage
    sessionStorage.setItem("allUsers_limit", JSON.stringify(limit));
    sessionStorage.setItem("allUsers_page", currentPage.toString());
    sessionStorage.setItem("allUsers_cardStatus", cardStatus);
    sessionStorage.setItem("allUsers_searchTerm", searchTerm);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [searchTerm, currentPage, cardStatus, limit, dispatch, setLoading, selectedCities, selectedProfessions, selectedPlans, advancedFilters, resolveStatusParams]);

  // Handler for Exporting All Users
  const handleExportAllUsersList = () => {
    try {
      setLoading(true);
      dispatch(allUsersList({
        search: searchTerm,
        ...resolveStatusParams(cardStatus),
        limit: 100000,
        city_id: selectedCities.map(c => c.value),
        profession_id: selectedProfessions.map(p => p.value),
        plan_id: selectedPlans.map(p => p.value),
        advancedFilters,
      })).then((res) => {
        const data = res?.payload?.data?.allUsers;
        if (!data || data.length === 0) {

          toast.success("No data available to Export");
          setLoading(false);
          return;
        }

        // Map the data for export
        const dataToExport = data?.map((item) => {
          return {
            Name: item?.profile?.name || "N/A",
            MobileNo: item?.mobile_number || "N/A",
            Email: item?.email || "N/A",
            Designation: item?.profile?.designation || "N/A",
            BankName: item?.profile?.bank?.bank_name || "N/A",
            BranchCity: item?.profile?.branch?.branch_name || "N/A",
            Status: item?.status || "N/A",
            Companies: item?.company_count || "0",
            VerificationStatus: item?.verification_status === "verified" ? "Verified" : "Pending",
            SubscriptionPlan: item?.subscription_plan_name || "Free",
            CreatedAt: item?.created_at ? new Date(item.created_at).toLocaleString("en-IN", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            }) : "-",
            UpdatedAt: item?.updated_at ? new Date(item.updated_at).toLocaleString("en-IN", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            }) : "-",
          }
        })

        // Export to Excel
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "All Users");
        const fileName = "All Users(Admin).xlsx";
        XLSX.writeFile(workbook, fileName);
        setLoading(false);
        dispatch(allUsersList({
          search: searchTerm,
          ...resolveStatusParams(cardStatus),
          page: currentPage,
          limit: limit?.value,
          city_id: selectedCities.map(c => c.value),
          profession_id: selectedProfessions.map(p => p.value),
          plan_id: selectedPlans.map(p => p.value),
          advancedFilters,
        }))
      })
    } catch (error) {
      console.error("Error exporting data: ", error);
      toast.error("Error exporting data: ", error);
      setLoading(false); // Disable loading state on error
    }
  }

  return (
    <>

      <Container fluid className="px-0">
        {/* Card Section */}

        <div className="grid gap-3 mb-4">
          {displayCards?.map((item, i) => (<TabCard
            key={i}
            count={item?.total}
            title={item?.title}
            className={location.pathname === '/user/mep' ? 'g-col-2' : 'g-col-3'}
            icon={item?.icon}
            active={item.status === cardStatus}
            status={item?.status}
            iconClassName={item?.iconClass}
            onClick={() => handleCardClick(item)}

          />))}
        </div>
        <div className="bg-white rounded-2 overflow-hidden common-shadow mt-4">
          {/* List Section */}
          <ListSection
            key={location.pathname}
            data={allUsers}
            isLoading={isLoading}
            searchTerm={searchTerm}
            setCurrentPage={setCurrentPage}
            setSearchTerm={setSearchTerm}
            addUserHandler={addUserHandler}
            getCustomerActions={getCustomerActions}
            handleExportAllUsersList={handleExportAllUsersList}
            currentPage={currentPage}
            limit={limit}
            handleChange={handleChange}
            isInitialLoad={isInitialLoadRef.current}

            cardStatus={cardStatus}
            selectedCities={selectedCities}
            setSelectedCities={setSelectedCities}
            selectedProfessions={selectedProfessions}
            setSelectedProfessions={setSelectedProfessions}
            selectedPlans={selectedPlans}
            setSelectedPlans={setSelectedPlans}
            advancedFilters={advancedFilters}
            setAdvancedFilters={setAdvancedFilters}
            setFilterModal={setFilterModal}
            plans={plans}
            planOptions={planOptions}
          />

          {openChangePwModal && (
            <PasswordChangeModal
              isOpen={openChangePwModal}
              onClose={() => {
                setOpenChangePwModal(false);
                setChangePasswordId("");
              }}
              onSubmit={async (newPassword) => {
                const result = await dispatch(changeUserPassword({ id: changePasswordId?.id, password: newPassword }));
                if (changeUserPassword.rejected.match(result)) {
                  throw new Error(result.payload?.message || "Failed to change password");
                }
              }}
              title={`Change Password for ${changePasswordId?.first_name || ""} ${changePasswordId?.last_name || ""}`}
            />
          )}
          {/* Add User Modal */}
        </div>
      </Container>
      <FilterModal
        isOpen={filterModal}
        onClose={() => setFilterModal(false)}
        title="Filters"
        size="lg"
        filterValues={{
          city: selectedCities.map(c => c.value),
          profession: selectedProfessions.map(p => p.value)
        }}
        setFilterValues={(updater) => {
          const currentValues = {
            city: selectedCities.map(c => c.value),
            profession: selectedProfessions.map(p => p.value)
          };
          const next = typeof updater === 'function' ? updater(currentValues) : updater;

          if (next.city !== undefined) {
            const newCities = cityOptions.filter(c => next.city.includes(c.value));
            setSelectedCities(newCities);
          }
          if (next.profession !== undefined) {
            const newProfessions = professionOptions.filter(p => next.profession.includes(p.value));
            setSelectedProfessions(newProfessions);
          }
        }}
        cityOptions={cityOptions}
        professionOptions={professionOptions}
        setCurrentPage={setCurrentPage}
        onCityInputChange={handleCitySearch}
      />
    </>
  );
};

export default AllUsers;


